import { useCallback, useEffect, useRef, useState } from 'react';

import { CancelError, CancellablePromise } from '@atlassian/dynamic-cards-search-client';

import { type SelectedFiltersType } from '../../../common/constants/types';
import { type onError, useLogException } from '../../error-boundary';
import { useSelectedFilters } from '../../filters/selected';
import { useInputQuery } from '../../store';
import {
	type TState as BootstrapState,
	type Features,
	useBootstrap,
	useFeatures,
} from '../../store/bootstrap';
import {
	useDropdownActions,
	useDropdownLoading,
	usePrefetchedRecentSearchResults,
	usePrefetchedRecentViewsResults,
} from '../../store/dropdown';
import { conditionalRendering } from '../conditional-rendering';
import { fetchAutoSuggestionResults } from '../fetch-auto-suggestion-results';
import { fetchPeopleAndTeamsResults } from '../fetch-people-and-teams-results';
import { fetchQuickSearchResults } from '../fetch-quick-search-results';
import { filterRecentSearchResults } from '../fetch-recent-search-results';
import { filterRecentViewsResults } from '../fetch-recent-views-results';

type FetchAllReturnType = Awaited<ReturnType<typeof fetchAll>>;
type FetchAllArgs = {
	bootstrap: BootstrapState;
	features?: Features;
	onError: onError;
	query: string;
	selectedEntities: string[];
	selectedFilters: SelectedFiltersType;
};

const fetchAll = async ({
	query,
	bootstrap,
	onError,
	features,
	selectedFilters,
	selectedEntities,
}: FetchAllArgs) => {
	const resolveEmpty = () => Promise.resolve({ results: [], duration: 0 });

	return Promise.all([
		features?.peopleAndTeamsSearch?.enabled
			? fetchPeopleAndTeamsResults(query, bootstrap, onError)
			: resolveEmpty(),
		features?.autoSuggestion?.enabled
			? fetchAutoSuggestionResults(query, bootstrap, onError)
			: resolveEmpty(),
		features?.quickSearch?.enabled
			? fetchQuickSearchResults({
					query,
					experience: 'company-hub-search',
					bootstrap,
					onError,
					selectedFilters,
					selectedEntities,
				})
			: resolveEmpty(),
	]);
};

export const useFetchAll = () => {
	const [inputQuery] = useInputQuery();
	const [bootstrap] = useBootstrap();
	const [dropdownLoading] = useDropdownLoading();
	const requestRef = useRef<CancellablePromise<FetchAllReturnType>>();
	const [currentInputQuery, setCurrentInputQuery] = useState<string>(inputQuery);
	const [recentSearchResults] = usePrefetchedRecentSearchResults();
	const [recentViewsResults] = usePrefetchedRecentViewsResults();
	const [features] = useFeatures();

	const logException = useLogException();

	const {
		setPeopleAndTeamsResults,
		setRecentViewsResults,
		setAutoSuggestionResults,
		setRecentSearchResults,
		setQuickSearchResults,
		setDropdownLoading,
		setResultCount,
	} = useDropdownActions();

	const { selectedFilters, entitiesForResultsCountQuery } = useSelectedFilters({
		outboundAuthConfigs: bootstrap.outboundAuthConfigs,
	});

	const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchData = useCallback(
		async (query: string, bootstrap: BootstrapState) => {
			setCurrentInputQuery(inputQuery);
			if (requestRef.current) {
				// If there is an existing fetchAll promise, cancel it
				requestRef.current.cancel();
			}
			setDropdownLoading(true);

			// Set the fetchAll promise
			requestRef.current = new CancellablePromise(
				fetchAll({
					query,
					bootstrap,
					onError: logException,
					features,
					selectedFilters,
					selectedEntities: entitiesForResultsCountQuery,
				}),
			);
			try {
				const [peopleAndTeamsResults, autoSuggestionResults, quickSearchResults] =
					await requestRef.current.promise();

				// update store
				setPeopleAndTeamsResults(peopleAndTeamsResults);
				setAutoSuggestionResults(autoSuggestionResults);
				const filteredRecentSearchResults = filterRecentSearchResults(recentSearchResults, query);
				setRecentSearchResults(filteredRecentSearchResults);
				setQuickSearchResults(quickSearchResults.results);
				const filteredRecentViewsResults = filterRecentViewsResults(
					recentViewsResults ?? [],
					query,
				);
				setRecentViewsResults(filteredRecentViewsResults);

				const resultCounts = conditionalRendering(query, {
					peopleAndTeams: peopleAndTeamsResults.results.length,
					autoSuggestion: autoSuggestionResults.results.length,
					quickSearch: quickSearchResults.results.length,
					recentSearch: filteredRecentSearchResults.length,
					recentViews: filteredRecentViewsResults.length,
				});
				setResultCount(resultCounts);

				// stop loading
				setDropdownLoading(false);
			} catch (error) {
				if (!(error instanceof CancelError)) {
					// stop loading
					setDropdownLoading(false);
					// log error
					logException(error);
				}
			}
		},
		[
			inputQuery,
			logException,
			recentSearchResults,
			recentViewsResults,
			setAutoSuggestionResults,
			setDropdownLoading,
			setPeopleAndTeamsResults,
			setRecentSearchResults,
			setRecentViewsResults,
			setResultCount,
			setQuickSearchResults,
			features,
			selectedFilters,
			entitiesForResultsCountQuery,
		],
	);

	useEffect(() => {
		if (
			(inputQuery !== currentInputQuery || (dropdownLoading && !requestRef.current)) &&
			recentViewsResults !== undefined
		) {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current);
			}

			debounceTimeout.current = setTimeout(() => {
				fetchData(inputQuery, bootstrap);
			}, 300);
		}
	}, [inputQuery, bootstrap, currentInputQuery, dropdownLoading, recentViewsResults, fetchData]);
};
